import { useContext } from 'react';

import { WorkflowCard } from '@/components/workflow/workflow-card';
import { ROUTES } from '@/constants/routes';
import { useWorkflowSummary } from '@/hooks/queries/workflow/use-workflow-summary';
import { useOrgNavigate } from '@/hooks/use-org-navigate';

import { AgentChatContext } from './agent-chat-provider';

/**
 * Resolves the click handler for a workflow card wherever one might render --
 * inside an agent-chat surface, or from the generic markdown renderer, which
 * has no provider at all in some hosts (composer chat, collection pages).
 * `useContext` reads null in that case rather than throwing, and the card
 * just always offers to open the workflow.
 *
 * Withheld entirely when the workflow is already open beside the chat: there
 * is nowhere for a click to take the user, and the card is left as a summary
 * of what the canvas already shows.
 */
export function useWorkflowCardOpen(
  workflowId: string,
): (() => void) | undefined {
  const navigate = useOrgNavigate();
  const chatContext = useContext(AgentChatContext);
  if (chatContext?.visibleWorkflowId === workflowId) return undefined;
  return () => {
    if (chatContext?.onOpenWorkflow) {
      chatContext.onOpenWorkflow(workflowId);
      return;
    }
    navigate(`${ROUTES.WORKFLOW.ROOT}/${workflowId}`);
  };
}

/**
 * The workflow a turn looked one up about.
 *
 * A turn holds one workflow card at a time: this is the state before a launch,
 * which the run card takes over from the moment the user starts the workflow.
 *
 * `visible` is what the turn is waiting on rather than whether to render at
 * all, so the summary is fetched while the reply is still streaming and the
 * card can paint from cache the moment the turn settles.
 */
export function ChatWorkflowCard({
  workflowId,
  visible = true,
}: {
  workflowId: string;
  visible?: boolean;
}) {
  const { summary } = useWorkflowSummary(workflowId);
  const onOpen = useWorkflowCardOpen(workflowId);

  // Nothing stands in for the card while it resolves: the turn's own text
  // already names the workflow, so an empty frame beneath it only shifts the
  // conversation around.
  if (!visible || !summary) return null;

  return (
    <WorkflowCard
      testId='chat-workflow-card'
      name={summary.name}
      description={summary.description}
      isPublic={summary.isPublic}
      nodeCount={summary.nodeCount}
      lastRunAt={summary.lastRunAt}
      clientData={summary.clientData}
      collaborators={summary.collaborators}
      onOpen={onOpen}
    />
  );
}
